
import { Prop, Component, Ref, Model, mixins, Watch, Emit } from 'nuxt-property-decorator';
import { TranslateResult } from '../../../../types/i18n';
import { Size } from '../../../../types';
import Hint from '../../../Hint.vue';
import { Collapsible } from '../../../../components/layout';
import InputTextMixin from './InputTextMixin';

@Component({ components: { Collapsible, Hint } })
export default class InputText extends mixins(InputTextMixin) {
  @Prop({ type: String, default: 'text' }) readonly type!: string;
  @Prop({ type: String, default: 'md' }) readonly size!: string;
  @Prop({ type: String, default: '' }) readonly leadingText!: string | TranslateResult;
  @Prop({ type: String, default: '' }) readonly leadingIcon!: string;
  @Prop({ type: Boolean, default: false }) readonly useMask!: boolean;
  @Prop({ type: Boolean, default: false }) readonly hideRequiredMark!: boolean;
  @Prop({ type: Boolean, default: false }) readonly noTrim!: boolean;

  readonly Sizes = Size;

  @Ref() inputElement?: HTMLInputElement;

  @Model('input', { type: [String, Number], default: null }) readonly value!: string | number | null;

  @Emit('input')
  emitInput(newValue: string | number, trim = false) {
    const formattedValue = trim ? String(newValue).trim() : newValue;
    if (this.useMask && this.leadingText) {
      return formattedValue ? `${this.leadingText}${formattedValue}` : '';
    }
    return formattedValue;
  }

  get innerValue() {
    if (!this.value) return '';
    if (this.leadingText && this.useMask) {
      return String(this.value).replace(String(this.leadingText), '');
    }
    return this.value;
  }

  set innerValue(newValue: string | number) {
    this.emitInput(newValue);
  }

  get inputClassNames() {
    return {
      '-destructive': this.hasValidationError,
      '-with-leading-text': this.leadingText,
      '-with-leading-icon': this.leadingIcon,
    };
  }

  get helpTextId(): string {
    return `${this.inputId}-help`;
  }

  get errorTextId(): string {
    return `${this.inputId}-error`;
  }

  get ariaDescribedBy(): string | undefined {
    const ids: string[] = [];
    if (this.hasValidationError) ids.push(this.errorTextId);
    if (this.help) ids.push(this.helpTextId);
    return ids.length ? ids.join(' ') : undefined;
  }

  async mounted() {
    await this.$nextTick();
    if (this.autofocus) this.focus();
  }

  @Watch('innerValue.length')
  onValueChange(newValue: string | number, oldValue: string | number) {
    if (newValue !== oldValue && this.hasValidationError) this.triggerValidation();
  }

  public async onBlur() {
    this.$emit('blur');
    // emit trimmed value on blur only avoid trims on every keypress
    // wait for v-model to update before triggering validation
    if (!this.noTrim && this.innerValue) {
      this.emitInput(this.innerValue, true);
      await this.$nextTick();
    }
    // prevent required validation on blur
    if (this.innerValue) this.triggerValidation();
  }

  public focus() {
    this.inputElement?.focus();
  }
}
